import React, { useEffect, useRef, useState } from 'react';
import {
  EllipsisVerticalIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  BanknotesIcon,
  DocumentTextIcon,
  UserGroupIcon,
  ClockIcon,
  BoltIcon
} from '@heroicons/react/24/outline';
import { clsx } from 'clsx';
export interface ClientActionGroupProps {
  onView: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onRegisterPayment: () => void;
  onViewInvoices: () => void;
  onViewFamily: () => void;
  onViewHistory: () => void;
  /** Activar servicio por fallecimiento (modal existente) */
  onActivateService?: () => void;
}

const iconBtn =
  'inline-flex h-9 w-9 items-center justify-center rounded-lg border border-transparent text-slate-500 transition-colors hover:border-slate-200 hover:bg-slate-50 hover:text-slate-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-300';

export const ClientActionGroup: React.FC<ClientActionGroupProps> = ({
  onView,
  onEdit,
  onDelete,
  onRegisterPayment,
  onViewInvoices,
  onViewFamily,
  onViewHistory,
  onActivateService
}) => {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  return (
    <div className="flex items-center justify-end gap-1" ref={wrapRef}>
      <button type="button" className={iconBtn} title="Ver detalle" aria-label="Ver detalle" onClick={onView}>
        <EyeIcon className="h-5 w-5" />
      </button>
      <button type="button" className={iconBtn} title="Editar cliente" aria-label="Editar cliente" onClick={onEdit}>
        <PencilIcon className="h-5 w-5" />
      </button>

      <div className="relative">
        <button
          type="button"
          className={clsx(iconBtn, open && 'border-slate-200 bg-slate-50')}
          title="Más acciones"
          aria-expanded={open}
          aria-haspopup="true"
          onClick={() => setOpen((v) => !v)}
        >
          <EllipsisVerticalIcon className="h-5 w-5" />
        </button>

        {open && (
          <div
            className="absolute right-0 z-40 mt-1 w-52 origin-top-right rounded-xl border border-slate-200/90 bg-white py-1 shadow-lg shadow-slate-900/10 ring-1 ring-slate-100"
            role="menu"
          >
            <MenuItem icon={BanknotesIcon} label="Registrar pago" onClick={() => { setOpen(false); onRegisterPayment(); }} />
            <MenuItem icon={DocumentTextIcon} label="Ver facturas" onClick={() => { setOpen(false); onViewInvoices(); }} />
            <MenuItem icon={UserGroupIcon} label="Ver familiares" onClick={() => { setOpen(false); onViewFamily(); }} />
            <MenuItem icon={ClockIcon} label="Historial de pagos" onClick={() => { setOpen(false); onViewHistory(); }} />
            {onActivateService && (
              <MenuItem
                icon={BoltIcon}
                label="Activar servicio"
                onClick={() => {
                  setOpen(false);
                  onActivateService();
                }}
              />
            )}
            <div className="my-1 border-t border-slate-100" />
            <button
              type="button"
              role="menuitem"
              className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm font-medium text-rose-600 hover:bg-rose-50"
              onClick={() => {
                setOpen(false);
                onDelete();
              }}
            >
              <TrashIcon className="h-4 w-4 shrink-0" />
              Eliminar cliente
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

const MenuItem: React.FC<{
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  onClick: () => void;
}> = ({ icon: Icon, label, onClick }) => (
  <button
    type="button"
    role="menuitem"
    className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm font-medium text-slate-700 hover:bg-slate-50"
    onClick={onClick}
  >
    <Icon className="h-4 w-4 shrink-0 text-slate-400" />
    {label}
  </button>
);
